// CONSOLA-03 — regla ÚNICA de capacidades para menú, rutas y acciones del frontend.
// La autorización EFECTIVA vive en el backend (tenancy.AccessContext + route_registry): esto sólo
// evita ofrecer acciones que el backend va a rechazar. Espeja el contrato vigente:
//   · internal supervisor  -> administración de Galarya (sitios, dispositivos, espacios, usuarios,
//     organizaciones, alta de personas).
//   · internal monitorista -> consulta y atención operativa; NINGUNA mutación de catálogo.
//   · external institucional -> sólo los permisos de su rol (person.manage / contact.manage…),
//     dentro de su organización y de sus sites concedidos. NO se concede autogestión nueva aquí.
// `cliente`/pagador es relación comercial y nunca otorga capacidades.
export function capacidades(user) {
  const u = user || null;
  const externo = !!u && u.actor_type === "external";
  const interno = !!u && !externo;
  const supervisor = interno && u.role === "supervisor";
  const permisos = (u && u.permissions) || [];
  const tiene = (p) => (externo ? permisos.includes(p) : interno);

  return {
    interno,
    externo,
    supervisor,
    // Administración interna de Galarya (equivale a require_internal_supervisor en backend).
    verUsuarios: supervisor,
    gestionarUsuarios: supervisor,
    verOrganizaciones: supervisor,
    administrarOrganizacion: supervisor,
    // Catálogo: sitios, dispositivos y espacios son de Galarya (require_supervisor en backend).
    crearSitio: supervisor,
    editarSitio: supervisor,
    gestionarDispositivos: supervisor,
    gestionarEspacios: supervisor,
    // Personas y contactos: supervisor interno o institucional con el permiso correspondiente.
    // CONSOLA-02/03 r2 (C-1/C-3): las acciones de la ficha NO comparten autorización, así que
    // cada una espeja la guarda REAL de su endpoint en vez de una sola capacidad de "editar":
    //   · PUT /api/personas            -> interno (monitorista incluido) o external person.manage.
    //   · PUT /api/personas (sitio_id) -> sólo supervisor interno (403 explícito al monitorista).
    //   · PUT /api/dispositivos        -> require_supervisor (la asociación persona<->dispositivo
    //     se hace por ese endpoint, así que NO se ofrece a quien recibirá 403).
    //   · PUT /api/personas/{id}/plan-respuesta -> require_supervisor.
    //   · POST /api/contactos          -> ruta INTERNA (cualquier interno); external no la tiene.
    crearPersona: supervisor,
    editarDatosPersona: supervisor || interno || tiene("person.manage"),
    editarInstruccionesPersona: supervisor || interno || tiene("person.manage"),
    reubicarPersona: supervisor,
    asociarDispositivosPersona: supervisor,
    // v2.13.4 (decisión de Dirección): el Plan de respuesta, los contactos y la política de
    // Recordatorios son EXPEDIENTE OPERATIVO: los captura y corrige cualquier monitorista interna
    // de Galarya/VESA. NO se creó rol nuevo y NO se les concedió nada estructural: reubicar,
    // asociar hardware, activar/desactivar y la administración de sitios/organizaciones siguen
    // siendo supervisor-only (arriba), igual que en el backend.
    editarPlanRespuesta: supervisor || interno || tiene("person.manage"),
    cambiarEstadoPersona: supervisor,
    gestionarContactos: supervisor || interno || tiene("contact.manage") || tiene("person.manage"),
    crearContacto: interno || tiene("contact.manage") || tiene("person.manage"),
    //   · v2.13.0 Programados: gestionar la tarea = misma autorización que editar la ficha/plan de
    //     la persona (person.manage); registrar el resultado = capacidad operativa (case.operate).
    //     NO se creó rol ni permiso nuevo.
    gestionarProgramados: supervisor || interno || tiene("person.manage"),
    operarProgramados: interno || tiene("case.operate"),
    verProgramados: interno || tiene("case.read"),
    // Lectura y operación: no se recorta para esconder edición.
    verSitios: interno || tiene("site.read"),
    verPersonas: interno || tiene("person.read"),
    operarCasos: interno || tiene("case.operate"),
  };
}

// CONSOLA-03: rutas que sólo existen para la administración interna. Se usan en el menú
// (soloSupervisor) y en el guard de ruta de App.js (SoloSupervisor).
export const RUTAS_SOLO_SUPERVISOR = ["/usuarios", "/organizaciones", "/pendientes", "/ws101-credenciales"];

// UI-009: transiciones del expediente administrativo. No cambia la máquina de estados: refleja sus
// precondiciones (borrador -> en_revision -> validado) para no ofrecer un paso ya consumido.
export function accionesExpediente(onboardingStatus, caps) {
  const estado = onboardingStatus || "borrador";
  return {
    puedeEnviarARevision: !!caps.administrarOrganizacion && estado === "borrador",
    puedeValidar: !!caps.administrarOrganizacion && estado === "en_revision",
  };
}
